import { periodsOverlap } from "@swivl-mono/helpers";
import { WeeklyPeriod } from "@swivl-mono/types";
import { Button, ButtonVariant, ErrorText } from "@swivl-mono/ui";
import { TbX } from "react-icons/tb";

import { DAY_ABBR, OVERLAP_ERROR, hhmmToMinutes, minutesToHHMM, nextWindowAfter, summarizeWeek } from "./scheduleTime";

const TIME_INPUT_CLASS =
  "body-sm rounded-sm border border-grey-400 px-2 py-1 text-grey-900 hover:border-orange-500 focus:border-orange-500 outline-none";

interface WeeklyGridProps {
  periods: WeeklyPeriod[];
  onChange: (next: WeeklyPeriod[]) => void;
}

/**
 * 7-row weekly schedule editor, shown only while a section is being edited.
 * Each day lists its open windows with time inputs, and can be closed or given
 * extra windows for split shifts. The read-only view is `WeeklySummary`.
 *
 * Time inputs are native `<input type="time">` for keyboard accessibility and
 * locale-aware 12h/24h display; the stored value is minutes since midnight.
 */
export function WeeklyGrid({ periods, onChange }: WeeklyGridProps): React.ReactElement {
  const periodsByDay = (dow: number): WeeklyPeriod[] => periods.filter((p) => p.dayOfWeek === dow).sort((a, b) => a.openMinute - b.openMinute);

  const updatePeriod = (idx: number, next: Partial<WeeklyPeriod>): void => {
    onChange(periods.map((p, i) => (i === idx ? { ...p, ...next } : p)));
  };

  const removePeriodAt = (idx: number): void => onChange(periods.filter((_, i) => i !== idx));

  const addPeriodForDay = (dow: number): void => {
    const newWindow = nextWindowAfter(periodsByDay(dow), { openMinute: 9 * 60, closeMinute: 17 * 60 });
    onChange([...periods, { dayOfWeek: dow as WeeklyPeriod["dayOfWeek"], ...newWindow }]);
  };

  const closeDay = (dow: number): void => onChange(periods.filter((p) => p.dayOfWeek !== dow));

  return (
    <div>
      {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
        const dayPeriods = periodsByDay(dow);
        const isClosed = dayPeriods.length === 0;
        return (
          <div key={dow} className="mx-4 grid grid-cols-[200px_1fr] items-start gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
            <span className="body-sm-alt py-1.5 text-grey-900">{DAY_ABBR[dow]}</span>

            {isClosed ? (
              <div className="flex items-center gap-3">
                <span className="body-sm py-1.5 text-grey-900">Closed</span>
                <Button variant={ButtonVariant.TEXT} onClick={() => addPeriodForDay(dow)}>
                  Open this day
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {dayPeriods.map((p) => {
                  const periodIdx = periods.indexOf(p);
                  return (
                    <div key={periodIdx} className="flex items-center gap-2">
                      <input
                        type="time"
                        aria-label={`${DAY_ABBR[dow]} opening time`}
                        value={minutesToHHMM(p.openMinute)}
                        onChange={(e) => updatePeriod(periodIdx, { openMinute: hhmmToMinutes(e.target.value) })}
                        className={TIME_INPUT_CLASS}
                      />
                      <span className="body-sm text-grey-500">–</span>
                      <input
                        type="time"
                        aria-label={`${DAY_ABBR[dow]} closing time`}
                        value={minutesToHHMM(p.closeMinute)}
                        onChange={(e) => updatePeriod(periodIdx, { closeMinute: hhmmToMinutes(e.target.value) })}
                        className={TIME_INPUT_CLASS}
                      />
                      <button
                        type="button"
                        onClick={() => removePeriodAt(periodIdx)}
                        className="cursor-pointer text-grey-400 hover:text-red-500"
                        title="Remove this window"
                        aria-label={`Remove ${DAY_ABBR[dow]} window`}
                      >
                        <TbX className="size-4" />
                      </button>
                    </div>
                  );
                })}
                {periodsOverlap(dayPeriods) && <ErrorText>{OVERLAP_ERROR}</ErrorText>}
                <div className="flex items-center gap-4">
                  <Button variant={ButtonVariant.TEXT} onClick={() => addPeriodForDay(dow)}>
                    Add window
                  </Button>
                  <Button variant={ButtonVariant.TEXT} onClick={() => closeDay(dow)}>
                    Mark closed
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Read-only view of a week, one row per day. */
export function WeeklySummary({ periods }: { periods: WeeklyPeriod[] }): React.ReactElement {
  return (
    <div>
      {summarizeWeek(periods).map((row) => (
        <div key={row.day} className="mx-4 grid grid-cols-[200px_1fr] items-center gap-x-4 border-b border-grey-200 py-3 last:border-b-0">
          <span className="body-sm-alt text-grey-900">{row.day}</span>
          <span className="body-sm text-grey-900">{row.hours}</span>
        </div>
      ))}
    </div>
  );
}
